import { useState } from 'react';
import { faqItems, testimonials } from '@/mocks/comics';
import Breadcrumb from '@/components/feature/Breadcrumb';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const organizationSchema = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '古风漫画网',
    url: 'https://www.gufengmanhua.com',
    description: '古风漫画网不仅提供免费的韩国漫画在线阅读，还独具特色地推荐兼具古风韵味的热门韩漫。',
    foundingDate: '2023',
    contactPoint: {
      '@type': 'ContactPoint',
      contactType: 'customer service',
      email: 'contact@gufengmanhua.com',
      availableLanguage: ['Chinese'],
    },
  };

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: faqItems.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: {
        '@type': 'Answer',
        text: item.answer,
      },
    })),
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const honeypotField = form.querySelector<HTMLInputElement>('input[name="website_alt"]');
    if (honeypotField && honeypotField.value.trim() !== '') {
      setFormStatus('success');
      return;
    }

    const formData = new FormData(form);
    if (honeypotField) {
      formData.delete('website_alt');
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9osud0senbniolesl80', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const msg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后重试';
        if (msg.toLowerCase().includes('spam') || msg.includes('form data is spam')) {
          setFormError('提交失败，请稍后重试');
        } else {
          setFormError(msg);
        }
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <script type="application/ld+json">{JSON.stringify(organizationSchema)}</script>
      <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>

      <div className="max-w-[1400px] mx-auto px-4 md:px-6 pt-24 pb-14">
        <Breadcrumb items={[{ label: '首页', href: '/' }, { label: '关于我们' }]} />

        <section className="mb-14">
          <h1 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900 mb-6 text-center">
            关于古风漫画网
          </h1>
          <div className="max-w-3xl mx-auto">
            <div className="bg-background-100 rounded-xl p-6 md:p-8 mb-8">
              <h2 className="font-heading text-lg font-bold text-foreground-900 mb-4">品牌故事</h2>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                古风漫画网成立于2023年，是一家专注于韩国漫画免费在线阅读的中文平台。我们以"古风美学"为核心设计理念，
                将传统东方美学与现代漫画阅读体验完美融合，为中文读者打造一个兼具视觉美感与阅读便捷性的漫画天地。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                作为一个深受韩漫爱好者喜爱的免费韩国漫画在线阅读网站，古风漫画网始终坚持"免费、优质、便捷"的服务理念。
                我们精心收录了奇幻冒险、恋爱言情、动作热血、悬疑推理、古风奇幻、搞笑日常等多个分类的热门韩漫，
                让每一位读者都能找到心仪的作品。
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed">
                我们独创的下拉式观看模式，让漫画阅读体验更加流畅自然。无需频繁点击翻页，只需轻轻向下滑动，
                精美的漫画画面便会连续呈现，如同翻阅一卷卷古风画卷，带您沉浸在精彩的漫画世界中。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
              <div className="bg-background-100 rounded-lg p-5 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 mx-auto mb-3">
                  <i className="ri-book-read-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">海量资源</h3>
                <p className="text-xs text-foreground-500">收录数百部热门韩国漫画，每日持续更新</p>
              </div>
              <div className="bg-background-100 rounded-lg p-5 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 mx-auto mb-3">
                  <i className="ri-shield-check-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">完全免费</h3>
                <p className="text-xs text-foreground-500">所有漫画永久免费，无需付费即可畅读全部内容</p>
              </div>
              <div className="bg-background-100 rounded-lg p-5 text-center">
                <div className="w-12 h-12 flex items-center justify-center rounded-full bg-secondary-50 text-secondary-500 mx-auto mb-3">
                  <i className="ri-smartphone-line text-xl"></i>
                </div>
                <h3 className="font-heading text-base font-semibold text-foreground-900 mb-1">多端适配</h3>
                <p className="text-xs text-foreground-500">支持手机、平板、电脑，随时随地畅享阅读</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 text-center">
            用户口碑
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-5 max-w-4xl mx-auto">
            {testimonials.map((t) => (
              <div key={t.id} className="bg-background-100 rounded-lg p-5">
                <div className="flex items-center gap-1 mb-3">
                  {Array.from({ length: 5 }, (_, i) => (
                    <i
                      key={i}
                      className={`text-sm ${i < t.rating ? 'ri-star-fill text-accent-500' : 'ri-star-line text-foreground-300'}`}
                    ></i>
                  ))}
                </div>
                <p className="text-sm text-foreground-600 leading-relaxed mb-3">{t.content}</p>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-medium text-foreground-800">{t.name}</span>
                  <span className="text-xs text-foreground-400">{t.date}</span>
                </div>
              </div>
            ))}
          </div>
        </section>

        <section className="mb-14">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 text-center">
            常见问题
          </h2>
          <div className="max-w-3xl mx-auto space-y-3">
            {faqItems.map((item, idx) => (
              <details key={idx} className="bg-background-100 rounded-lg group">
                <summary className="flex items-center justify-between p-4 md:p-5 cursor-pointer">
                  <span className="text-sm font-medium text-foreground-800 pr-4">{item.question}</span>
                  <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                </summary>
                <div className="px-4 md:px-5 pb-4 md:pb-5">
                  <p className="text-sm text-foreground-600 leading-relaxed">{item.answer}</p>
                </div>
              </details>
            ))}
          </div>
        </section>

        <section className="max-w-2xl mx-auto">
          <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 text-center">
            联系我们
          </h2>
          <div className="bg-background-100 rounded-xl p-6 md:p-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-6 mb-8">
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-primary-50 text-primary-500 flex-shrink-0">
                  <i className="ri-mail-line"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">电子邮箱</h3>
                  <p className="text-sm text-foreground-500">contact@gufengmanhua.com</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 flex items-center justify-center rounded-full bg-accent-50 text-accent-500 flex-shrink-0">
                  <i className="ri-customer-service-2-line"></i>
                </div>
                <div>
                  <h3 className="text-sm font-semibold text-foreground-800 mb-1">客服时间</h3>
                  <p className="text-sm text-foreground-500">周一至周五 9:00 - 18:00</p>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} data-readdy-form="true" className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="contact-name" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    姓名
                  </label>
                  <input
                    type="text"
                    id="contact-name"
                    name="name"
                    required
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的姓名"
                  />
                </div>
                <div>
                  <label htmlFor="contact-email" className="block text-sm font-medium text-foreground-700 mb-1.5">
                    邮箱
                  </label>
                  <input
                    type="email"
                    id="contact-email"
                    name="email"
                    required
                    className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                    placeholder="请输入您的邮箱"
                  />
                </div>
              </div>
              <div>
                <label htmlFor="contact-subject" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  主题
                </label>
                <input
                  type="text"
                  id="contact-subject"
                  name="subject"
                  required
                  className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                  placeholder="请输入主题"
                />
              </div>
              <div>
                <label htmlFor="contact-message" className="block text-sm font-medium text-foreground-700 mb-1.5">
                  留言内容
                </label>
                <textarea
                  id="contact-message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  className="w-full px-3 py-2.5 text-sm bg-background-50 border border-background-200 rounded-md focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                  placeholder="请输入您的留言（500字以内）"
                ></textarea>
                <p className="text-xs text-foreground-400 mt-1">最多500个字符</p>
              </div>
              <input
                type="text"
                name="website_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="honeypot-field"
              />
              <div className="flex flex-col sm:flex-row items-center gap-3">
                <button
                  type="submit"
                  className="px-6 py-2.5 bg-primary-500 text-background-50 rounded-md font-medium text-sm hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap w-full sm:w-auto"
                >
                  发送留言
                </button>
                {formStatus === 'success' && (
                  <span className="text-sm text-green-600">消息已发送，我们会尽快回复您！</span>
                )}
                {formStatus === 'error' && (
                  <span className="text-sm text-red-500">{formError}</span>
                )}
              </div>
            </form>
          </div>
        </section>
      </div>
    </>
  );
}